// src/activity/TimelineReport.jsx — System → Activity → Timeline: everything people did, newest first.
// Filters live in the URL (?q=&user=&section=&kind=&from=&to=) so a view can be linked.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, BlockStack, InlineStack, Text, TextField, Select, Banner, Button, Badge, Spinner, Box } from '@shopify/polaris';
import { useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../AuthComponent';
import { ReportDateRange, paneHeightToFitScroller } from '../Reporting';

const KIND_LABEL = { signin: 'Sign-in', signout: 'Sign-out', signin_failed: 'Failed sign-in', view: 'View', denied: 'Refused', action: 'Action' };
const KIND_TONE = { signin: 'success', signout: undefined, signin_failed: 'critical', view: 'info', denied: 'critical', action: undefined };
const DOT = { ok: '#2e9e5b', bad: '#d72c0d', info: '#8a9aa0' };
const FILTERS = ['q', 'user', 'section', 'kind'];
const roleLabel = (csv) => (csv || '').split(',').filter(Boolean).map(x => x === 'pin_user' ? 'PIN user' : x.replace(/_/g, ' ')).join(', ') || '—';
const when = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });

export default function TimelineReport() {
    const fetchAuth = useAuthFetch();
    const [sp, setSp] = useSearchParams();
    const filters = useMemo(() => ({ ...Object.fromEntries(FILTERS.map(k => [k, sp.get(k) || ''])), since: sp.get('from') || '', until: sp.get('to') || '' }), [sp]);
    const [q, setQ] = useState(filters.q);
    const [items, setItems] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [choices, setChoices] = useState({ users: [], sections: [], kinds: [] });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const wrapRef = useRef(null);
    const [paneH, setPaneH] = useState(null);

    const set = useCallback((patch) => setSp(prev => {
        const n = new URLSearchParams(prev);
        Object.entries(patch).forEach(([k, v]) => { if (v) n.set(k, v); else n.delete(k); });
        return n;
    }, { replace: true }), [setSp]);
    useEffect(() => { const t = setTimeout(() => { if (q !== filters.q) set({ q }); }, 350); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

    const params = useCallback((extra = {}) => {
        const p = new URLSearchParams();
        Object.entries({ ...filters, ...extra }).forEach(([k, v]) => { if (v) p.set(k, v); });
        return p;
    }, [filters]);

    const load = useCallback(async (before) => {
        setLoading(true); setErr(null);
        try {
            const r = await fetchAuth(`/api/activity?${params(before ? { before: String(before) } : {})}&limit=100`);
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
            const j = await r.json();
            setItems(prev => before ? [...prev, ...j.items] : j.items);
            setHasMore(j.has_more); setChoices({ users: j.users, sections: j.sections, kinds: j.kinds });
        } catch (e) { setErr(String(e.message || e)); } finally { setLoading(false); }
    }, [fetchAuth, params]);
    useEffect(() => { load(null); }, [load]);

    useLayoutEffect(() => {
        const measure = () => { const el = wrapRef.current; if (!el) return; const h = paneHeightToFitScroller(el); setPaneH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h); };
        measure(); const t = setTimeout(measure, 300);
        window.addEventListener('resize', measure);
        return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
    }, [items.length, paneH]);
    useEffect(() => { document.body.classList.add('report-flush-bottom'); return () => document.body.classList.remove('report-flush-bottom'); }, []);

    const exportCsv = async () => {
        try {
            const r = await fetchAuth(`/api/activity.csv?${params()}`);
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob(); const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = 'activity.csv'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) { setErr(String(e.message || e)); }
    };
    const opt = (list, all) => [{ value: '', label: all }, ...list.map(v => ({ value: v, label: KIND_LABEL[v] || v }))];
    const personOpts = [{ value: '', label: 'Everyone' }, ...(choices.users || []).map(u => ({ value: u.username, label: u.name }))];

    return (
        <BlockStack gap="300">
            {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <ReportDateRange dateFrom={filters.since} setDateFrom={v => set({ from: v })} dateTo={filters.until} setDateTo={v => set({ to: v })} />
                    </InlineStack>
                    <InlineStack gap="200" blockAlign="end" wrap>
                        <div style={{ width: 240 }}><TextField label="Search" labelHidden placeholder="Search actions and references" value={q} onChange={setQ} autoComplete="off" clearButton onClearButtonClick={() => { setQ(''); set({ q: '' }); }} /></div>
                        <div style={{ width: 170 }}><Select label="Person" labelHidden options={personOpts} value={filters.user} onChange={v => set({ user: v })} /></div>
                        <div style={{ width: 170 }}><Select label="Section" labelHidden options={opt(choices.sections, 'All sections')} value={filters.section} onChange={v => set({ section: v })} /></div>
                        <div style={{ width: 150 }}><Select label="Kind" labelHidden options={opt(choices.kinds, 'All kinds')} value={filters.kind} onChange={v => set({ kind: v })} /></div>
                        
                        <Button onClick={exportCsv}>Export CSV</Button>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">Everything people do in OpsHub, newest first: sign-ins, actions, page views and refused attempts. Background traffic such as webhooks and heartbeats is left out.</Text>
                </BlockStack>
            </Card>
            <Card padding="0">
                <div ref={wrapRef} style={{ overflow: 'auto', maxHeight: paneH || undefined }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720, fontSize: 13 }}>
                        <thead style={{ position: 'sticky', top: 0, background: 'var(--p-color-bg-surface, #fff)', zIndex: 1 }}>
                            <tr>{['', 'When', 'Person', 'Role', 'Section', 'Kind', 'What happened'].map((h, i) => <th key={i} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--p-color-border, #ddd)', whiteSpace: 'nowrap' }}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                            {items.map(r => (
                                <tr key={r.id}>
                                    <td style={{ padding: '6px 4px 6px 12px', width: 14 }}><span title={r.tone} style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: DOT[r.tone] || DOT.info }} /></td>
                                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{when(r.at)}</td>
                                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }} title={r.username}>{r.name || r.username}</td>
                                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: 'var(--p-color-text-secondary, #6d7175)' }} title="The role this person held when they did it">{roleLabel(r.roles)}</td>
                                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{r.section}</td>
                                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}><Badge tone={KIND_TONE[r.kind]}>{KIND_LABEL[r.kind] || r.kind}</Badge></td>
                                    <td style={{ padding: '6px 10px' }}>{r.action}{r.source === 'imported' ? ' ' : ''}{r.source === 'imported' ? <Badge>From the log</Badge> : null}</td>
                                </tr>
                            ))}
                            {!items.length && !loading && <tr><td colSpan={7} style={{ padding: 24, color: 'var(--p-color-text-secondary, #6d7175)' }}>Nothing matches these filters.</td></tr>}
                        </tbody>
                    </table>
                    <Box padding="300">
                        <InlineStack align="center">
                            {loading ? <Spinner size="small" /> : hasMore ? <Button onClick={() => load(items[items.length - 1].id)}>Load more</Button> : null}
                        </InlineStack>
                    </Box>
                </div>
            </Card>
        </BlockStack>
    );
}
